import React from 'react';

import { css, keyframes } from '@emotion/react';
import { CssVars, Markdown, Modal, NeutralColorToken, TextButton, Type } from '@robinhood/design';
import { ModalHeader } from '@robinhood/design/product/components/Modal';

import CardReveal from './CardReveal';
import LinkButton from './LinkButton';
import ValuePropsCell from './ValuePropsCell';
import logger from 'src/clients/LoggingClient';
import { Domain, fetch, getApiDomain } from 'src/clients/Network/http/fetch';
import { Event, track } from 'src/clients/tracking';
import Button from 'src/components/creditApplication/Button';
import CreditApplicationPageLayout from 'src/components/creditApplication/CreditApplicationPageLayout';
import Divider from 'src/components/creditApplication/Divider';
import GradientBackground from 'src/components/creditApplication/GradientBackground';
import { useIdentiAgreement } from 'src/hooks/useIdentiAgreement';
import { useGlobalStore } from 'src/state/global';

const CardholderAgreementUrl = `${getApiDomain(Domain.RHCreditCard)}/legal/terms-offer/`;
const ElectronicDisclosuresUrl = `${getApiDomain(Domain.RHCreditCard)}/legal/credit-score-notice/`;
const RobinhoodGoldAgreementUrl = 'https://cdn.robinhood.com/assets/robinhood/legal/Robinhood%20Gold%20Agreement.pdf';
const RewardsTermsUrl = 'https://api.robinhood.com/creditcard/legal/reward-terms';

const GoldAgreementType = 'gold_agreement';

// Override LinkRenderer to open up links in a new tab
const MarkdownLinkRenderer = ({ children, href, ...rest }: { href: string; children: string | Array<string> }) => {
  return (
    <TextButton href={href} label={children} type='anchor' target='_blank' color={NeutralColorToken.Fg1} {...rest} />
  );
};

interface PropsIface {
  onNext: () => void;
}

const FinalTermsPage = ({ onNext }: PropsIface) => {
  const agreement = useIdentiAgreement({ type: GoldAgreementType });
  const creditApplicationId = useGlobalStore((s) => s.creditApplicationId);
  const [showFinalTerms, setShowFinalTerms] = React.useState(false);
  const [showGoldAgreement, setShowGoldAgreement] = React.useState(false);

  const onAnimationComplete = React.useCallback(() => {
    setShowFinalTerms(true);
  }, []);

  const onGoldAgreement = React.useCallback(() => {
    setShowGoldAgreement(true);
  }, []);

  const onContinue = React.useCallback(async () => {
    try {
      if (!!agreement.sha256 && !!agreement.id) {
        const res = await fetch(Domain.RHIdenti, `/user_info/agreements/v2/sign/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            agreement_id: agreement.id,
            sha256: agreement.sha256,
          }),
        });
        if (res.ok) {
          const json = await res.json();
          if (json.agreement_log_id != null) {
            track(Event.IdentiAgreementSigned, { agreementLogId: json.agreement_log_id, agreementId: agreement.id });
            logger.info('Identi agreement signed', {
              agreementLogId: json.agreement_log_id,
              agreementId: agreement.id,
            });
          }
        }
      } else {
        track(Event.IdentiAgreementSigningError, { error: 'Missing agreement id or sha256' });
      }
    } catch (e) {
      track(Event.IdentiAgreementSigningError, { agreementId: agreement.id });
      logger.error(e, { source: 'FinalTermsPage.onContinue', agreementId: agreement.id });
    }
    track(Event.RhAccountTermsAndConditionsAccepted, { creditApplicationId });
    onNext();
  }, [agreement.id, agreement.sha256, onNext, creditApplicationId]);

  return (
    <div css={container}>
      <GradientBackground>
        {!showFinalTerms ? (
          <CardReveal animationState='in' onComplete={onAnimationComplete} />
        ) : (
          <div css={fadeIn}>
            <CreditApplicationPageLayout title='Your Robinhood Gold&nbsp;Card'>
              <div css={root}>
                <ValuePropsCell />
                <div css={agreements}>
                  <Type.Text size='S' color={NeutralColorToken.Fg2}>
                    By tapping "Continue," you are agreeing to the following documents and a hard credit pull. You
                    understand you are providing your electronic signature to enter into legal agreements.
                  </Type.Text>
                  <div css={linksContainer}>
                    <LinkButton
                      label='Cardholder Agreement & APR Details'
                      href={`${CardholderAgreementUrl}${creditApplicationId}`}
                    />
                    <LinkButton
                      label='Credit Score Notice'
                      href={`${ElectronicDisclosuresUrl}${creditApplicationId}`}
                    />
                    {!agreement.text ? (
                      <LinkButton label='Robinhood Gold Agreement' href={RobinhoodGoldAgreementUrl} />
                    ) : (
                      <TextButton
                        variant='standalone'
                        type='button'
                        label={'Robinhood Gold Agreement'}
                        color={NeutralColorToken.Fg1}
                        onClick={onGoldAgreement}
                        icon={{
                          type: 'LinkOut16',
                          side: 'end',
                        }}
                      />
                    )}
                  </div>
                </div>
                <Divider />
                <div>
                  <Type.Text size='S' color={NeutralColorToken.Fg2}>
                    <TextButton
                      variant='inline'
                      type='anchor'
                      label='See rewards terms'
                      href={RewardsTermsUrl}
                      target='_blank'
                      color={NeutralColorToken.Fg2}
                    />
                    . Robinhood Gold Card is offered by Robinhood Credit, Inc. and is issued by Coastal Community Bank,
                    pursuant to a license from Visa U.S.A. Inc. Robinhood Gold is a subscription-based membership
                    program of premium services offered through Robinhood Gold, LLC.
                  </Type.Text>
                </div>
                <Button label='Continue' width='full' type='submit' onClick={onContinue} />
              </div>
            </CreditApplicationPageLayout>
          </div>
        )}
      </GradientBackground>
      {showGoldAgreement && !!agreement.text && (
        <Modal width='extraExtraWide' onClose={() => setShowGoldAgreement(false)}>
          <ModalHeader title='Robinhood Gold Agreement' />
          <Markdown source={agreement.text} overrideRenderers={{ link: MarkdownLinkRenderer }} />
          <div style={{ paddingBottom: 32 }} />
        </Modal>
      )}
    </div>
  );
};

const fadeInAnimation = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const container = css`
  width: 100vw;
  height: 100vh;
  background-color: ${CssVars.neutral.bg1};
`;

const fadeIn = css`
  animation: ${fadeInAnimation} 0.8s ease-out forwards;
`;

const root = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  text-align: center;
`;

const agreements = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const linksContainer = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
`;

export default FinalTermsPage;
